import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { trackScrollDepth } from "@/lib/analytics";

const THRESHOLDS = [25, 50, 75, 100] as const;

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const firedRef = useRef<Set<number>>(new Set());
  // Cache document height & viewport height om FORCED SYNCHRONOUS LAYOUT
  // te voorkomen: iedere scroll-frame `document.documentElement.scrollHeight`
  // lezen na een React-render dwingt de browser tot een synchrone reflow.
  // We meten alleen bij mount, op resize en via een ResizeObserver op <body>.
  const docHeightRef = useRef<number>(0);
  const viewportHeightRef = useRef<number>(0);

  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const scrollable = docHeightRef.current - viewportHeightRef.current;
    if (scrollable <= 0) return;
    const percent = Math.min(100, Math.round((scrollTop / scrollable) * 100));

    for (const t of THRESHOLDS) {
      if (percent >= t && !firedRef.current.has(t)) {
        firedRef.current.add(t);
        trackScrollDepth(`${t}%`, pathname);
      }
    }
  }, [pathname]);

  useEffect(() => {
    window.scrollTo(0, 0);
    firedRef.current = new Set();

    // Send pageview to Google Analytics after SEO component has updated document.title
    setTimeout(() => {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_path: pathname,
          page_title: document.title,
          page_location: window.location.href,
        });
      }
    }, 100);

    // Initial measure (na paint zodat de layout stabiel is).
    const measure = () => {
      docHeightRef.current = document.documentElement.scrollHeight;
      viewportHeightRef.current = window.innerHeight;
    };
    const rafId = requestAnimationFrame(measure);

    // ResizeObserver op <body> vervangt de per-scroll-frame layout-read.
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        // Batch: measure buiten de scroll-handler → geen forced reflow.
        docHeightRef.current = document.documentElement.scrollHeight;
      });
      ro.observe(document.body);
    }
    const onResize = () => {
      viewportHeightRef.current = window.innerHeight;
      docHeightRef.current = document.documentElement.scrollHeight;
    };
    window.addEventListener("resize", onResize, { passive: true });

    // Throttled scroll listener (~1 check/frame, alleen cached reads).
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
    };
  }, [pathname, handleScroll]);

  return null;
};

export default ScrollToTop;
